// ════════════════════════════════════════════════════════════════════════════
//  ManDana — one project's records, and how they are looked at
//  ---------------------------------------------------------------------------
//  The Workshop's catalogue (md-workshop.ts) answers "every project you have".
//  This one answers "everything filed under THIS project" — and answering it
//  truthfully means reaching into two places at once:
//
//      the backlog     md:projectRecords, the thought captured with no day yet
//      the days        every day document, the hour already booked
//
//  A note written on the train and a session on Tuesday are the same work at
//  two ages. The old project page drew them as two lists under two headings,
//  which is the app telling somebody their own work is two kinds of thing.
//  ONE BOARD, BOTH LISTS. A card crosses it as the work moves, and the day it
//  gets is a property of the card rather than which list it was filed on.
//
//  ── THE THREE SUBTRACTIONS ARE NOT SUBTRACTIONS ───────────────────────────
//  Inside one project, its project, its pursuit and its domain are constants.
//  So they are not fields here — not filterable, not groupable, not a property
//  to show or hide — and the controls that would offer them have nothing to
//  offer rather than something to refuse. A control whose answer can never
//  change is furniture. That is requirements 4, 5 and 6, settled by what this
//  catalogue IS rather than by three `if`s in the surface.
//
//  ── NOT ONE LINE OF THE ENGINE ────────────────────────────────────────────
//  Filtering, sorting, grouping, the saved-view document, the facet counts and
//  the memo are md-views.ts's, exactly as they are the Workshop's. This file is
//  the six answers a catalogue owes, and nothing else. If it ever grows a
//  filter loop, the loop is in the wrong file.
//
//  ── ONE SET OF VIEWS PER PROJECT ──────────────────────────────────────────
//  Cutting weight and Bookkeeping are not looked at the same way, so each keeps
//  its own views — the SCOPED case md-views.ts's scope exists for. The key is
//  `md:recordViews:{pid}` and the document `record-views:{pid}`.
//
//  NOT `md:projectViews:{pid}`, which is the obvious name and is a trap: the
//  Workshop's own document is `md:projectViews`, undo scopes match by PREFIX,
//  and every project's views would then land inside the Projects window's ⌘Z.
//  Pressing undo in one window would quietly rewrite the other.
// ════════════════════════════════════════════════════════════════════════════

type MdWorklistField = 'name' | 'status' | 'day' | 'time' | 'created';
type MdWorklistGroupBy = 'status';

(function (g: any) {
  'use strict';

  const CATALOGUE = 'project-records';
  const KEY = 'md:recordViews';
  const DOC = 'record-views';

  /* The order here is the order of the columns in a list: what it is, where it
     stands, when it runs, how much of it there is, and when it began. */
  const FIELDS: MdWorklistField[] = ['name', 'status', 'day', 'time', 'created'];
  /* One axis, and it is the only one that could be more than one column of the
     same thing. Grouping by day is a timeline drawn badly; grouping by project
     is one column. */
  const GROUP_BY: MdWorklistGroupBy[] = ['status'];
  const ZOOMS = ['day', 'week', 'month', 'quarter'];

  const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;


  /** One record, as a row.

      ── NO RECORD IS DATELESS ─────────────────────────────────────────────
      `day` always answers, because every record has one true fallback: the day
      it was written. A thing captured on Tuesday and never scheduled did happen
      on Tuesday — that is a fact about it, not a blank waiting to be filled —
      so the board can say when, the timeline can draw it, and sorting by day
      never has an empty half to push to the end.

      `scheduled` is the OTHER question, and the row keeps both because they are
      not the same one. It is the day somebody chose, and it is null until they
      choose it. Everything that must not be true of a record merely because it
      exists hangs off `scheduled`: its status (a captured thought is in the
      Backlog, not Active, however old it is), and its time given (an unbooked
      thought was given no hours, however long ago it was written). Collapsing
      the two would quietly empty the Backlog column and start counting hours
      nobody spent. */
  function rowOf(record: any, scheduled: string | null, ctx: any): any {
    const minutes = scheduled && g.mdRecordHasPassed(scheduled, record.min, record.dur,
      ctx && ctx.todayKey, ctx && ctx.nowMin)
      && typeof record.dur === 'number' && Number.isFinite(record.dur) && record.dur > 0
      ? record.dur : 0;
    /* md-record.ts owns this now (WR-1). It used to be worked out here from
       `created`, which meant the day a record came from depended on where its
       READER was standing; it is written down at creation instead. */
    const created = g.mdRecordOriginDay(record);
    return {
      id: record.uid,
      uid: record.uid,
      record,
      scheduled,
      day: scheduled || created,
      name: typeof record.name === 'string' && record.name.trim()
        ? record.name : (g.MD_RECORD_PLACEHOLDER || 'New Record'),
      ...statusOf(record, scheduled, ctx),
      min: typeof record.min === 'number' && Number.isFinite(record.min) ? record.min : null,
      dur: typeof record.dur === 'number' && Number.isFinite(record.dur) && record.dur > 0
        ? record.dur : null,
      minutes,
      created,
      createdAt: typeof record.created === 'number' ? record.created : 0,
      at: typeof record.at === 'number' ? record.at : 0,
    };
  }

  /** Every record filed under one project, from wherever it lives.

      ONE WALK, ONE CARD. The dated copy wins if a uid somehow appears in both
      places — a state the app does not write, since scheduling REMOVES the
      backlog row, but one another device could be halfway through sending. Two
      cards for one record is worse than either card being slightly stale. */
  function rowsFor(ctx: any): any[] {
    const pid = ctx && typeof ctx.projectId === 'string' ? ctx.projectId : '';
    if (!pid) return [];
    const out: any[] = [];
    const seen = new Set<string>();

    for (const map of [(ctx && ctx.events) || {}, (ctx && ctx.records) || {}]) {
      for (const day of Object.keys(map)) {
        if (!DAY_RE.test(day)) continue;              // never trust a key we did not write
        const list = map[day];
        if (!Array.isArray(list)) continue;
        for (const record of list) {
          if (!record || typeof record !== 'object' || record.pid !== pid) continue;
          if (typeof record.uid !== 'string' || !record.uid || seen.has(record.uid)) continue;
          seen.add(record.uid);
          out.push(rowOf(record, day, ctx));
        }
      }
    }

    const backlog = Array.isArray(ctx && ctx.projectRecords) ? ctx.projectRecords : [];
    for (const record of backlog) {
      if (!record || typeof record !== 'object' || record.pid !== pid) continue;
      if (typeof record.uid !== 'string' || !record.uid || seen.has(record.uid)) continue;
      seen.add(record.uid);
      out.push(rowOf(record, null, ctx));
    }
    return out;
  }

  /** What one field of one row reads as, for a filter or a sort. `null` is the
      one and only empty — md-views.ts tests for that and nothing else. */
  function cellOf(row: any, field: string): unknown {
    switch (field) {
      case 'name': return row.name || null;
      case 'status': return row.status;
      case 'day': return row.day;
      case 'time': return row.minutes || null;
      case 'created': return row.created;
      default: return null;
    }
  }

  /* Every status is a column whether or not anything is in it — a column you
     cannot drop onto is a broken promise.

     THE COLUMNS ARE THE PROJECT'S WORKFLOW (WR-4). This used to declare four
     words of its own — Backlog · Active · Parked · Done — which meant a person
     could write a whole vocabulary on the Statuses page and this board would go
     on showing them somebody else's. It is asked of the project rather than of
     the rows because an EMPTY project has columns too: a board with no cards
     still has to say where a card could go.

     ONE WORKFLOW BY CONSTRUCTION, so WF-13's spine collapse does not arise
     here. Every record on this board reads through this project, so every one
     of them reads through this workflow.

     The category rides along, exactly as the Workshop's does: a column head
     draws the spine's mark, and asking it to look the status up again is how a
     head and the cards under it end up resolved against two workflows. */
  function axisBuckets(groupBy: string, _rows: any[], ctx?: any): { id: string; label: string }[] {
    if (groupBy !== 'status') return [];
    const flow = g.mdWorkflowFor
      ? g.mdWorkflowFor('project', ctx && ctx.projectId) : null;
    if (!flow) return [];
    return flow.statuses.map((row: any) => ({ id: row.id, label: row.name,
      category: row.category, color: row.color }));
  }

  /* NO EMPTY COLUMN ON THE STATUS AXIS. "Not saying" retired with the spine
     (WF-2): absence resolves to the workflow's Inbox on read, so there is no
     longer a record anywhere that is standing on nothing, and a column for that
     would be a place nothing could ever land. */
  const EMPTY_LABEL: Record<string, string> = {};

  /** The status one record's row wears, resolved through its workflow and
      reconciled with its day's tick. md-project.ts owns that reconciliation;
      this only asks for it, so a card in a Completed column and a line ticked
      in a morning cannot come from two different rules.

      THE NAME AND THE HUE TRAVEL WITH THE ID, exactly as they do on a project's
      row: a cell that redrew the word from the id would have to guess which
      workflow to resolve it against, and a card resolved one way beside a
      column head resolved another is how a board starts lying quietly. */
  function statusOf(record: any, scheduled: string | null, ctx: any):
  { status: string | null; statusName: string; statusCategory: string | null;
    statusColor: string | null } {
    const stood = g.mdRecordStatusOf
      ? g.mdRecordStatusOf(record, scheduled, ctx && ctx.completed) : null;
    return { status: stood ? stood.id : null, statusName: stood ? stood.name : '',
      statusCategory: stood ? stood.category : null,
      statusColor: (stood && stood.color) || null };
  }

  /* THE BOARD IS FIRST, so it is what the window opens on: requirement 3. The
     ids are fixed, because a view remembered as "the one I was on" must be the
     same view on the next device — and they are prefixed apart from the
     Workshop's own `viewboard`, since the two sets are read by the same engine
     and a shared id is a bug waiting for a shared cache. */
  /* `created` is a field but NOT one of the four shown by default. It is a
     date, and a date drawn under a record's name reads as the day that record
     is ON — so a thought captured this morning with no day yet showed "18 Aug"
     and looked scheduled. The one it is honest to draw there is `day`, and the
     rest of the fields are one Display press away. */
  const SHOWN = ['name', 'status', 'day', 'time'];

  const DEFAULTS = () => [
    { id: 'recboard', name: 'Board', type: 'board', groupBy: 'status', visibleFields: [...SHOWN] },
    { id: 'reclist', name: 'List', type: 'list', visibleFields: [...SHOWN] },
    { id: 'rectime', name: 'Timeline', type: 'timeline', timelineZoom: 'month',
      visibleFields: [...SHOWN] },
  ];

  g.mdViewsRegister({
    id: CATALOGUE,
    key: KEY,
    doc: DOC,
    fields: FIELDS,
    groupBy: GROUP_BY,
    zooms: ZOOMS,
    /* Asked, not fixed: the ladder a sort reads is a vocabulary somebody
       writes, and it does not exist when this catalogue registers itself. */
    declared: { status: () => (g.mdWorkflowStatusLadder ? g.mdWorkflowStatusLadder() : []) },
    numeric: ['time'],
    buckets: axisBuckets,
    emptyLabel: EMPTY_LABEL,
    defaults: DEFAULTS,
    rows: rowsFor,
    cell: cellOf,
    /* The project id is a STRING, and md-views.ts counts a primitive memo part
       for itself. Without it here, one project's rows would be handed to the
       next project asked for in the same render. */
    memo: (ctx: any) => [ctx && ctx.projectId, ctx && ctx.events, ctx && ctx.records,
      ctx && ctx.projectRecords, ctx && ctx.completed],
  });

  /* ── the doors ────────────────────────────────────────────────────────────
     md-views.ts with the catalogue already named. The ones that reach STORAGE
     take the project id first, because that is the scope; the ones that reach
     ROWS do not, because the id is already in the ctx they are handed — and
     asking for it twice is asking two questions that can disagree. */

  const mdWorklistSanitiseView = (value: unknown) => g.mdViewsSanitise(CATALOGUE, value);
  const mdWorklistViews = (pid: unknown) => g.mdViewsList(CATALOGUE, pid);
  const mdWorklistViewById = (pid: unknown, id: unknown) => g.mdViewsById(CATALOGUE, id, pid);
  const mdWorklistActiveId = (pid: unknown) => g.mdViewsActiveId(CATALOGUE, pid);
  const mdWorklistSetActive = (pid: unknown, id: unknown) => g.mdViewsSetActive(CATALOGUE, id, pid);
  const mdWorklistViewCreate = (pid: unknown, type: unknown, name?: unknown, extras?: unknown) =>
    g.mdViewsCreate(CATALOGUE, type, name, extras, pid);
  const mdWorklistViewPatch = (pid: unknown, id: unknown, patch: unknown) =>
    g.mdViewsPatch(CATALOGUE, id, patch, pid);
  const mdWorklistViewDelete = (pid: unknown, id: unknown) => g.mdViewsDelete(CATALOGUE, id, pid);

  /** Which projects have written any views. Read off STORAGE rather than off
      the project list, because a document is exactly what there is to send:
      a project nobody has changed a view on has nothing to say, and a document
      left over from a deleted project still has to travel so the other device
      can be told it is gone. */
  function scopes(): string[] {
    const prefix = `${KEY}:`;
    return (g.mdStorageKeys ? g.mdStorageKeys(prefix) : [])
      .map((key: string) => key.slice(prefix.length))
      .filter(Boolean);
  }

  const mdWorklistSyncDocs = () => g.mdViewsSyncDocs(CATALOGUE, scopes());
  /* SCOPED OR NOTHING. md-views.ts also accepts the bare document name, which
     is right for the Workshop — one set of views for the account — and is not
     a document this catalogue has: `record-views` with no project on the end
     would write to a key nothing ever reads. Refused here rather than stored
     somewhere invisible. */
  const mdWorklistApplyDoc = (key: unknown, value: unknown) =>
    (typeof key === 'string' && key.startsWith(`${DOC}:`)
      ? g.mdViewsApplyDoc(CATALOGUE, key, value) : false);
  const mdWorklistRows = (view: unknown, ctx?: any) => g.mdViewsRows(CATALOGUE, view, ctx);
  const mdWorklistGroups = (view: unknown, ctx?: any) => g.mdViewsGroups(CATALOGUE, view, ctx);
  const mdWorklistFacetCounts = (view: unknown, ctx: any, field: unknown) =>
    g.mdViewsFacetCounts(CATALOGUE, view, ctx, field);

  g.MD_WORKLIST_KEY = KEY;
  g.MD_WORKLIST_DOC = DOC;
  g.MD_WORKLIST_CATALOGUE = CATALOGUE;
  g.MD_WORKLIST_FIELDS = FIELDS;
  g.MD_WORKLIST_GROUP_BY = GROUP_BY;
  Object.assign(g, { mdWorklistSanitiseView, mdWorklistViews, mdWorklistViewById,
    mdWorklistActiveId, mdWorklistSetActive, mdWorklistViewCreate, mdWorklistViewPatch,
    mdWorklistViewDelete, mdWorklistSyncDocs, mdWorklistApplyDoc,
    mdWorklistRows, mdWorklistGroups, mdWorklistFacetCounts });
})(typeof window !== 'undefined' ? window : globalThis);
